import React, { useState, useRef, useEffect } from 'react';
import styles from './Tooltip.module.css';
import clsx from 'clsx';

export interface TooltipProps {
    children: React.ReactElement;
    content: React.ReactNode;
    placement?: 'top' | 'bottom' | 'left' | 'right';
    className?: string;
    enterDelay?: number;
    leaveDelay?: number;
}

export const Tooltip: React.FC<TooltipProps> = ({
    children,
    content,
    placement = 'top',
    className,
    enterDelay = 0,
    leaveDelay = 0
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [isTouchDevice, setIsTouchDevice] = useState(false);
    const childRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    let enterTimeout: NodeJS.Timeout;
    let leaveTimeout: NodeJS.Timeout;

    useEffect(() => {
        // Check if it's a touch device
        const isTouchCapable =
            'ontouchstart' in window || navigator.maxTouchPoints > 0 || (navigator as any).msMaxTouchPoints > 0;

        setIsTouchDevice(isTouchCapable);

        // Add click outside listener for touch devices
        if (isTouchCapable) {
            const handleClickOutside = (event: MouseEvent | TouchEvent) => {
                if (isVisible && childRef.current && !childRef.current.contains(event.target as Node)) {
                    setIsVisible(false);
                }
            };

            document.addEventListener('touchstart', handleClickOutside);
            document.addEventListener('mousedown', handleClickOutside);

            return () => {
                document.removeEventListener('touchstart', handleClickOutside);
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isVisible]);

    const calculatePosition = () => {
        if (!childRef.current || !tooltipRef.current) return;

        const childRect = childRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const spacing = 8; // Space between tooltip and element

        let top = 0;
        let left = 0;

        switch (placement) {
            case 'top':
                top = childRect.top - tooltipRect.height - spacing;
                left = childRect.left + (childRect.width - tooltipRect.width) / 2;
                break;
            case 'bottom':
                top = childRect.bottom + spacing;
                left = childRect.left + (childRect.width - tooltipRect.width) / 2;
                break;
            case 'left':
                top = childRect.top + (childRect.height - tooltipRect.height) / 2;
                left = childRect.left - tooltipRect.width - spacing;
                break;
            case 'right':
                top = childRect.top + (childRect.height - tooltipRect.height) / 2;
                left = childRect.right + spacing;
                break;
        }

        // Adjust for scroll position
        top += window.scrollY;
        left += window.scrollX;

        setPosition({ top, left });
    };

    const handleMouseEnter = () => {
        if (isTouchDevice) return;
        clearTimeout(leaveTimeout);
        enterTimeout = setTimeout(() => {
            setIsVisible(true);
            requestAnimationFrame(calculatePosition);
        }, enterDelay);
    };

    const handleMouseLeave = () => {
        if (isTouchDevice) return;
        clearTimeout(enterTimeout);
        leaveTimeout = setTimeout(() => {
            setIsVisible(false);
        }, leaveDelay);
    };

    const handleClick = (e: React.MouseEvent | React.TouchEvent) => {
        if (!isTouchDevice) return;
        e.preventDefault();
        e.stopPropagation();
        setIsVisible(!isVisible);
        if (!isVisible) {
            requestAnimationFrame(calculatePosition);
        }
    };

    useEffect(() => {
        return () => {
            clearTimeout(enterTimeout);
            clearTimeout(leaveTimeout);
        };
    }, []);

    return (
        <>
            <div
                ref={childRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
                onTouchStart={handleClick}
                className={styles.tooltipTrigger}
            >
                {children}
            </div>
            <div
                ref={tooltipRef}
                className={clsx(
                    styles.tooltip,
                    styles[`tooltip${placement.charAt(0).toUpperCase() + placement.slice(1)}`],
                    className
                )}
                style={{
                    top: position.top,
                    left: position.left
                }}
                data-show={isVisible}
            >
                {content}
            </div>
        </>
    );
};
